import type {
  EnemyDefinition,
  EnemyEntranceDefinition,
  EnemyEntranceType,
  EntranceType,
  Facing,
  LevelDefinition,
  LevelSpawnSpot,
  LevelWave,
  SpawnSpotStyle,
  StageBackdrop,
  WaveSpawn
} from "./types";

export interface ResolvedEnemyEntrance {
  type: EnemyEntranceType;
  direction: Facing;
  duration: number;
  delay: number;
  backdrop: StageBackdrop;
  label: string;
  /** Painted feature the enemy arrives through. */
  spot?: LevelSpawnSpot;
  /** Order the enemy leaves its spot in. */
  queue: number;
  /** Where the entrance ends and the enemy starts fighting. */
  landing: { x: number; y: number };
}

const ENEMY_ENTRANCE_TYPES = new Set<EntranceType>([
  "walkIn",
  "doorway",
  "wallBurst",
  "climb",
  "vehicle"
]);

export function isEnemyEntranceType(type: EntranceType | undefined): type is EnemyEntranceType {
  return type !== undefined && ENEMY_ENTRANCE_TYPES.has(type);
}

export function isValidEnemyEntrance(entrance: EnemyEntranceDefinition | undefined): boolean {
  if (!entrance) return true;
  if (!ENEMY_ENTRANCE_TYPES.has(entrance.type)) return false;
  if (entrance.type === "vehicle" || entrance.type === "climb") {
    return entrance.type === "climb" ? !entrance.from?.length : Boolean(entrance.from?.length);
  }
  return true;
}

const VIEW_WIDTH = 960;
/** Brisk walk from a spawn spot to the landing, a little quicker than the enemies' own pace. */
export const ENTRANCE_WALK_SPEED = 125;
/** Seconds a climber takes to haul up over the ledge, mantle, and stand. */
export const CLIMB_DURATION = 1.1;
/** Heavies leap up from below the ledge instead of climbing. */
export const HEAVY_CLIMB_DURATION = 0.95;
/** Feet clearance a climber keeps above the ledge's lip once it stands on the platform. */
export const CLIMB_STAND_OFFSET = 12;

/**
 * Cybertruck geometry for the 320×106 sprite drawn facing right, measured from the middle of
 * the truck toward its nose.
 */
export const CYBERTRUCK = {
  width: 320,
  height: 106,
  /** Seconds from the wave start until the truck brakes to a stop at its spot. */
  arrive: 1.05,
  /** Front door, rear door, and bed centres. */
  exits: [44, -21, -105],
  /** Height of each exit's sill above the lane. */
  sills: [26, 26, 36],
  /** Seconds after the last passenger jumps before the truck pulls away. */
  linger: 0.6,
  /** Seconds it takes to drive off screen. */
  depart: 1.2
} as const;

/** Where passengers land along the truck, measured from its middle toward its nose. */
const CYBERTRUCK_DROPS: readonly number[] = [74, -34, -150, 132, 8, -196];
/** Passengers land at least this far in front of the lane the truck parks on. */
const CYBERTRUCK_CLEARANCE = 44;

export interface SpotTiming {
  /** Seconds the feature takes to open before the first enemy shows. */
  lead: number;
  /** Seconds between enemies using the same spot. */
  gap: number;
  /** Seconds an enemy spends coming out before it walks to its landing. */
  emerge: number;
}

export const SPOT_TIMING: Record<SpawnSpotStyle, SpotTiming> = {
  open: { lead: 0.12, gap: 0.45, emerge: 0.45 },
  tunnel: { lead: 0.3, gap: 0.55, emerge: 1.35 },
  rollup: { lead: 0.6, gap: 0.48, emerge: 0.45 },
  slide: { lead: 0.5, gap: 0.45, emerge: 0.45 },
  holo: { lead: 0.4, gap: 0.5, emerge: 0.6 },
  screen: { lead: 0.45, gap: 0.6, emerge: 1.05 },
  hatch: { lead: 0.35, gap: 0.65, emerge: 0.9 },
  wall: { lead: 0.32, gap: 0.6, emerge: 0.6 },
  glass: { lead: 0.32, gap: 0.6, emerge: 0.6 },
  shelf: { lead: 0.32, gap: 0.6, emerge: 0.6 },
  manhole: { lead: 0.4, gap: 0.8, emerge: 1.05 },
  cybertruck: { lead: CYBERTRUCK.arrive + 0.2, gap: 0.34, emerge: 0.45 }
};

/** Way the truck's nose points: it drives away from the side it arrives from. */
export function cybertruckFacing(spot: LevelSpawnSpot): Facing {
  return spot.direction === -1 ? 1 : -1;
}

/** Index into a truck's front door, rear door, and bed for the `queue`th passenger. */
export function cybertruckExit(queue: number): 0 | 1 | 2 {
  return (queue % 3) as 0 | 1 | 2;
}

/** Point on the floor where an enemy leaves its spawn spot and starts walking to its landing. */
export function spotExitPoint(spot: LevelSpawnSpot, queue: number): { x: number; y: number } {
  if (spot.style === "cybertruck") {
    const exit = cybertruckExit(queue);
    return { x: spot.x + cybertruckFacing(spot) * CYBERTRUCK.exits[exit], y: spot.y + 18 };
  }
  if (spot.style === "manhole") return { x: spot.x, y: spot.y + 18 };
  return { x: spot.x, y: (spot.floorY ?? spot.y) + 10 };
}

export function levelLedgeY(level: Pick<LevelDefinition, "ledgeY" | "groundBottom">): number {
  return level.ledgeY ?? level.groundBottom + 12;
}

/** Camera window a wave locks to, which every landing stays inside. */
export function waveView(level: LevelDefinition, wave: LevelWave): { left: number; right: number } {
  const left = Math.max(0, Math.min(wave.lockX - 590, level.width - VIEW_WIDTH));
  return { left, right: left + VIEW_WIDTH };
}

/** Least used spot from `from`, and how many enemies already used it this wave. */
function pickSpot(
  level: LevelDefinition,
  from: readonly string[] | undefined,
  queues: Map<string, number>
): { spot: LevelSpawnSpot; queue: number } | undefined {
  let best: { spot: LevelSpawnSpot; queue: number } | undefined;
  for (const id of from ?? []) {
    const spot = level.spawnSpots?.find((candidate) => candidate.id === id);
    if (!spot) continue;
    const queue = queues.get(id) ?? 0;
    if (!best || queue < best.queue) best = { spot, queue };
  }
  if (best) queues.set(best.spot.id, best.queue + 1);
  return best;
}

/** Sideways spread of enemies sharing a spot, in the order they leave it. */
const SPOT_FAN: readonly number[] = [0, -64, 64, -118, 118, -32, 32, -90];
/** Enemies leaving a doorway always end up at least this far in front of it. */
const SPOT_STEP_OUT = 12;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function lockLanding(
  level: LevelDefinition,
  wave: LevelWave,
  spawnIndex: number,
  side: Facing
): { x: number; y: number } {
  const offset = spawnIndex + 1;
  return {
    x: clamp(wave.lockX + side * (180 + (offset % 4) * 34), 80, level.width - 60),
    y:
      level.groundTop +
      25 +
      ((offset * 47) % Math.max(150, level.groundBottom - level.groundTop - 50))
  };
}

/**
 * Landing for an enemy leaving `spot`. It walks out to `depth`, the same spread of depths the
 * rest of the wave lands on, so a squad fills the floor instead of queueing at its door.
 */
function spotLanding(
  level: LevelDefinition,
  wave: LevelWave,
  spot: LevelSpawnSpot,
  queue: number,
  depth: number
): { x: number; y: number } {
  let x: number;
  let y: number;
  if (spot.style === "cybertruck") {
    const round = Math.floor(queue / CYBERTRUCK_DROPS.length);
    x = spot.x + cybertruckFacing(spot) * (CYBERTRUCK_DROPS[queue % CYBERTRUCK_DROPS.length]! + round * 26);
    y = Math.max(depth, spot.y + CYBERTRUCK_CLEARANCE);
  } else {
    const exit = spotExitPoint(spot, queue);
    const round = Math.floor(queue / SPOT_FAN.length);
    x = exit.x + SPOT_FAN[queue % SPOT_FAN.length]! + round * 30;
    y = spot.style === "manhole" ? depth : Math.max(depth, exit.y + SPOT_STEP_OUT);
  }
  const view = waveView(level, wave);
  return {
    x: clamp(x, Math.max(80, view.left + 56), Math.min(level.width - 60, view.right - 56)),
    y: clamp(y, level.groundTop + 8, level.groundBottom - 8)
  };
}

/** Climbers step a golden-ratio stride along the ledge so no two haul up in the same place. */
function climbLanding(level: LevelDefinition, wave: LevelWave, queue: number): { x: number; y: number } {
  const view = waveView(level, wave);
  const along = (0.5 + queue * 0.618034) % 1;
  const top = levelLedgeY(level) - CLIMB_STAND_OFFSET;
  return {
    x: Math.round(view.left + 70 + along * (VIEW_WIDTH - 140)),
    y: clamp(top - 10 - (queue % 3) * 16, level.groundTop + 8, level.groundBottom - 8)
  };
}

function travelSeconds(from: { x: number; y: number }, to: { x: number; y: number }): number {
  return Math.hypot(to.x - from.x, (to.y - from.y) * 1.25) / ENTRANCE_WALK_SPEED;
}

export function stageBackdropFor(levelId: string, lockX: number): StageBackdrop {
  if (levelId === "level04") return "horizon";
  if (levelId === "level03") return "corner";
  if (levelId === "level02") {
    if (lockX >= 2780) return "hangar";
    if (lockX >= 1800) return "tunnel";
    if (lockX >= 900) return "factory";
    return "street";
  }
  return "storefront";
}

export const HANGAR_DOOR = {
  x: 3640,
  y: 235,
  left: 3510,
  right: 3770,
  top: 138,
  height: 147
} as const;

export const TECHNOKING_ENTRANCE_DURATION = 6.8;
export const TECHNOKING_DOOR_BURN_DURATION = 2;
export const TECHNOKING_WALK_OUT_END = 4.15;
export const TECHNOKING_FLAME_ARC_START = 4.45;
export const TECHNOKING_FLAME_ARC_DURATION = 1.75;
export const TECHNOKING_SPEED_RAMP_START = 6;

export function technokingEntranceFrame(elapsed: number): number {
  if (elapsed < TECHNOKING_FLAME_ARC_START) {
    return 16 + (Math.floor(Math.max(0, elapsed) * 3.5) % 4);
  }
  const arcProgress = Math.max(
    0,
    Math.min(1, (elapsed - TECHNOKING_FLAME_ARC_START) / TECHNOKING_FLAME_ARC_DURATION)
  );
  if (arcProgress >= 1) return 47;
  return 40 + Math.min(6, Math.floor(arcProgress * 7));
}

export function hangarBossLanding(groundTop: number, groundBottom: number): { x: number; y: number } {
  return {
    x: 3472,
    y: Math.max(groundTop + 24, Math.min(358, groundBottom - 24))
  };
}

export function inHangarExit(x: number, y: number, groundTop: number, groundBottom: number): boolean {
  return x >= HANGAR_DOOR.x - 50 && x <= HANGAR_DOOR.right + 24 && y >= groundTop && y <= groundBottom;
}

export function defaultEnemyEntranceType(
  definition: EnemyDefinition,
  backdrop: StageBackdrop,
  spawnIndex: number
): EnemyEntranceType {
  if (definition.id === "technoking" && backdrop === "hangar") {
    return "doorway";
  }
  if (definition.archetype === "boss" || definition.archetype === "bruiser") {
    return "wallBurst";
  }
  if (backdrop === "factory" || backdrop === "tunnel" || backdrop === "hangar" || backdrop === "horizon") {
    return spawnIndex % 3 === 0 ? "wallBurst" : "doorway";
  }
  return spawnIndex % 2 === 0 ? "walkIn" : "doorway";
}

export function doorwayAnchor(x: number, backdrop: StageBackdrop): { x: number; y: number } {
  if (backdrop === "storefront") {
    const fronts = [330, 900, 1460, 2100];
    const front = fronts.reduce((best, current) =>
      Math.abs(current + 130 - x) < Math.abs(best + 130 - x) ? current : best
    );
    const leftDoor = front + 57;
    const rightDoor = front + 201;
    return {
      x: Math.abs(leftDoor - x) < Math.abs(rightDoor - x) ? leftDoor : rightDoor,
      y: 250
    };
  }
  if (backdrop === "factory") {
    const bay = Math.max(0, Math.min(3, Math.round((x - 1018) / 216)));
    return { x: 1018 + bay * 216, y: 230 };
  }
  if (backdrop === "tunnel") {
    return { x: 2220, y: 190 };
  }
  if (backdrop === "hangar") {
    return { x: HANGAR_DOOR.x, y: HANGAR_DOOR.y };
  }
  if (backdrop === "corner") {
    return { x, y: 238 };
  }
  if (backdrop === "horizon") {
    const rings = [360, 1280, 2160, 3180];
    const ring = rings.reduce((best, current) =>
      Math.abs(current - x) < Math.abs(best - x) ? current : best
    );
    return { x: ring, y: 236 };
  }
  const pylons = [186, 500, 780];
  const pylon = pylons.reduce((best, current) =>
    Math.abs(current - x) < Math.abs(best - x) ? current : best
  );
  return { x: pylon, y: 240 };
}

const LEDGE_QUEUE = "#ledge";

export function resolveEnemyEntrance(options: {
  level: LevelDefinition;
  wave: LevelWave;
  spawn: WaveSpawn;
  definition: EnemyDefinition;
  spawnIndex: number;
  side: Facing;
  /** Enemies already sent through each spawn spot this wave. Picking a spot counts it here. */
  queues?: Map<string, number>;
}): ResolvedEnemyEntrance {
  const { level, wave, definition, spawnIndex, side } = options;
  const queues = options.queues ?? new Map<string, number>();
  const backdrop = stageBackdropFor(level.id, wave.lockX);
  const specified = options.spawn.entrance ?? definition.entrance;
  const type = specified?.type ?? defaultEnemyEntranceType(definition, backdrop, spawnIndex);
  const base = {
    type,
    direction: specified?.direction ?? side,
    backdrop,
    label: definition.displayName
  };

  const picked = type === "climb" || type === "walkIn" ? undefined : pickSpot(level, specified?.from, queues);
  if (picked) {
    const { spot, queue } = picked;
    const timing = SPOT_TIMING[spot.style];
    const lane = lockLanding(level, wave, spawnIndex, side);
    const landing =
      definition.archetype === "boss" ? lane : spotLanding(level, wave, spot, queue, lane.y);
    return {
      ...base,
      spot,
      queue,
      landing,
      duration:
        specified?.duration ?? timing.emerge + travelSeconds(spotExitPoint(spot, queue), landing),
      delay: specified?.delay ?? timing.lead + queue * timing.gap
    };
  }

  if (type === "climb") {
    const queue = queues.get(LEDGE_QUEUE) ?? 0;
    queues.set(LEDGE_QUEUE, queue + 1);
    const landing = climbLanding(level, wave, queue);
    const heavy = definition.archetype === "bruiser" || definition.archetype === "boss";
    const top = { x: landing.x, y: levelLedgeY(level) - CLIMB_STAND_OFFSET };
    return {
      ...base,
      queue,
      landing,
      duration:
        specified?.duration ??
        (heavy ? HEAVY_CLIMB_DURATION : CLIMB_DURATION) + travelSeconds(top, landing),
      delay: specified?.delay ?? 0.2 + queue * 0.38
    };
  }

  const duration =
    specified?.duration ??
    (type === "wallBurst"
      ? definition.archetype === "boss"
        ? 0.88
        : 0.62
      : type === "doorway"
        ? definition.id === "technoking" && backdrop === "hangar"
          ? 1.08
          : 0.78
        : 0.95);
  return {
    ...base,
    queue: 0,
    landing: lockLanding(level, wave, spawnIndex, side),
    duration,
    delay: specified?.delay ?? spawnIndex * 0.18
  };
}
